import * as React from 'react';
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { useNavigate } from '@tanstack/react-router';
import { systemClock } from '@reos/config';
import { Bolt, Mail, Plus, User } from 'lucide-react';
import { PageHead } from '../components/page-head.js';
import { TaskColumn } from '../components/task-column.js';
import { TaskDrawer } from '../components/task-drawer.js';
import { MailCard } from '../components/mail-card.js';
import { MailboxStrip } from '../components/mailbox-strip.js';
import {
  getMailbox,
  getMailboxCards,
  replyMailCard,
  resolveMailCard,
  saveMailAttachment,
  type MailCardDto,
  type MailboxViewDto,
} from '../lib/mailbox-api.js';
import {
  listProposedEvents,
  suggestReplyDraft,
  type ProposedEventDto,
} from '../lib/inquiry-ai-api.js';
import { createCalendarEvent } from '../lib/calendar-api.js';
import { mailboxStrip, mailColumnCards, type MailPrimaryAction } from '../lib/mail-cards.js';
import { clientMailReasonKey } from '../lib/client-mail-message.js';
import { mailAttachmentReasonKey } from '../lib/mail-attachment-message.js';
import { createDemand } from '../lib/demands-api.js';
import {
  createTask,
  listTaskColumns,
  listTasks,
  runAutoTaskGeneration,
  transitionTask,
  type Task,
  type TaskColumn as TaskColumnModel,
} from '../lib/tasks-api.js';
import { listCases, type KanbanCase } from '../lib/cases-api.js';
import { listMembers, type TenantMember } from '../lib/members-api.js';
import { fetchPropertyAddresses } from '../lib/properties.js';
import { caseDisplayName, isInternalIdTitle } from '../lib/case-title.js';
import { DomainApiError } from '../lib/api-error.js';
import {
  decideTaskMove,
  fromDateTimeLocalValue,
  groupTasksByColumn,
  isDoneToday,
  matchesTaskQuery,
  matchesTaskSource,
  openTaskCount,
  overdueTaskCount,
  taskOriginKey,
  TASK_SOURCE_FILTERS,
  type TaskSourceFilter,
} from '../lib/task-board.js';
import { formatFullDate } from '../lib/relative-time.js';
import { readTaskViewPreference, useSession, writeTaskViewPreference } from '../stores/session.js';
import { useT } from '../lib/i18n.js';
import { DateTimeField } from '../components/date-field.js';
import { TaskList } from '../components/task-list.js';

/** Kolik úkolů nacte jedna stránka — hláska o oříznutí používá tuto hodnotu. */
const TASK_PAGE_LIMIT = 100;

/** Kolik zakázek se dotáhne kvůli štítkům na kartách (předloha: „Hrabanov, 3 parcely“). */
const CASE_PAGE_LIMIT = 100;

/** Zpoždění mezi psaním do hledání a dotazem na server (krok TOKYFIX2). */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Krok TAB1 bod 3 — KDO NESMÍ VIDĚT CELOU KANCELÁŘ.
 *
 * Zadání: „role `broker` vidí jen Moje“. Je to ROZHODNUTÍ O ZOBRAZENÍ, ne
 * o přístupu: server `GET /v1/crm/tasks` makléři úkoly kolegů vrátí (RLS je
 * na úrovni kanceláře, ne člověka). Tabule tedy přepínač nenabídne a filtruje
 * sama — kdyby to měla být hranice bezpečnosti, musel by ji držet server
 * a tohle by bylo jen zdání (viz `docs/security/threat-model.md`).
 */
const BROKER_ONLY_MINE_ROLES: ReadonlySet<string> = new Set(['broker']);

/**
 * Krok UXF5 bod 2 (nalez A2 / #33) — VYCHOZI POHLED JE SEZNAM, tabule je
 * volba. Re-audit GPT: pet sloupcu kanbanu necte jako pracovni fronta
 * (E05). Volba se pamatuje v predvolbe prohlizece (`reos.ukoly.view`,
 * `stores/session.ts` — jedine misto web-adminu, kde smi byt `localStorage`);
 * klic a jeho ctecku zavedl uz krok UXL1, jen ho zadna obrazovka nevolala.
 */
const TASK_VIEWS = ['list', 'kanban'] as const;
type TaskView = (typeof TASK_VIEWS)[number];

function readStoredTaskView(): TaskView {
  const raw = readTaskViewPreference();
  return raw !== null && (TASK_VIEWS as readonly string[]).includes(raw) ? (raw as TaskView) : 'list';
}

/**
 * `/ukoly` — tabule úkolů makléře podle SCHVÁLENÉ předlohy UX v3
 * `docs/design/ux-v3/tabule/Ukoly.png` (R.B. 3. 10. 2026 09:56).
 *
 * Sloupce: K udělání → Dělám → **Čeká na druhé** → **Hotovo dnes**. Jdou
 * z API (`/v1/crm/task-columns`), stejně jako povolené přechody
 * (`allowedTransitions` na kartě) — UI stavový automat nedoplňuje.
 *
 * Co krok TAB1 přidal proti CRM2:
 *  - hlavička s datem a „N otevřených · N po termínu“ (bod 2),
 *  - filtry Moje / Celá kancelář a čipy Vše / Automatické / Ruční (bod 3),
 *  - sloupec „Čeká na druhé“ a rozdělení hotových na dnešní a starší (bod 4),
 *  - karta podle předlohy s JEDNOU kontextovou akcí (bod 5).
 *
 * Krok AS1 bod 5: sloupec „Nové z e-mailu“, čip „E-mail“ a pás schránky.
 * Dokud makléř schránku nepřipojí, nevykreslí se NIC z toho — zadání TAB1
 * zakazuje „prázdný falešný sloupec“ i „demo kartu e-mailu“. Karty sloupce
 * vybírá `mailColumnCards` (tatáž funkce `emailCardsOnYou` jako Dnešek a MCP).
 */
export function UkolyPage(): React.JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  /*
   * Selektor musi vracet STABILNI referenci: `useSession` stoji na
   * `useSyncExternalStore`, takze `(s) => s.user?.roles ?? []` by pri kazdem
   * renderu vratil nove pole, React by videl zmenu snapshotu a renderoval
   * donekonecna („Maximum update depth exceeded"). Proto se cte cely `user`
   * (jedna reference ze store) a role se z nej odvodi.
   */
  const user = useSession((s) => s.user);
  const currentUserId = user?.id ?? null;
  const roles = user?.roles ?? [];
  /**
   * Makléř má „Celá kancelář“ zakázanou (viz `BROKER_ONLY_MINE_ROLES`).
   * `owner`/`admin` ji mají i tehdy, když je k tomu mají přiřazenou i roli
   * `broker` — jinak by vedoucí kanceláře přišel o pohled na svůj tým.
   */
  const brokerOnlyMine =
    roles.length > 0 && roles.every((role) => BROKER_ONLY_MINE_ROLES.has(role));

  const [tasks, setTasks] = React.useState<Task[] | null>(null);
  const [columns, setColumns] = React.useState<readonly TaskColumnModel[]>([]);
  const [cases, setCases] = React.useState<readonly KanbanCase[]>([]);
  const [members, setMembers] = React.useState<readonly TenantMember[]>([]);
  const [addressByProperty, setAddressByProperty] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [truncated, setTruncated] = React.useState(false);
  const [draggingAllowed, setDraggingAllowed] = React.useState<readonly string[] | null>(null);
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  /**
   * Krok TOKYFIX2 (překážka P2/6): hledání jde NA SERVER, ne jen přes načtenou
   * stránku. Tabule bere prvních `TASK_PAGE_LIMIT` úkolů podle termínu, takže
   * v kanceláři se stovkou automatických úkolů na dnes byl úkol s termínem za
   * tři dny pro makléřku nenajitelný — vypsaný nebyl a hledání ho taky
   * nenašlo, protože filtrovalo jen to, co už bylo v prohlížeči.
   *
   * Dotaz se posílá se zpožděním, aby každé písmeno neznamenalo dotaz.
   */
  const [searchQuery, setSearchQuery] = React.useState('');
  const [source, setSource] = React.useState<TaskSourceFilter | 'email'>('all');
  const [mailbox, setMailbox] = React.useState<MailboxViewDto | null>(null);
  const [mailCards, setMailCards] = React.useState<readonly MailCardDto[]>([]);
  const [busyMailId, setBusyMailId] = React.useState<string | null>(null);
  /** Krok MAIL1: výsledek odpovědi u JEDNÉ karty (odesláno / důvod, proč ne). */
  const [replyNotes, setReplyNotes] = React.useState<Readonly<Record<string, string>>>({});
  /** Krok PROP5 bod 5: výsledek „Uložit přílohu k zakázce" u JEDNÉ karty. */
  const [attachmentNotes, setAttachmentNotes] = React.useState<Readonly<Record<string, string>>>({});
  const [view, setView] = React.useState<TaskView>(() => readStoredTaskView());
  const [mineOnly, setMineOnly] = React.useState(brokerOnlyMine);
  const [showOlderDone, setShowOlderDone] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);
  const [autoBusy, setAutoBusy] = React.useState(false);
  const [busyTaskId, setBusyTaskId] = React.useState<string | null>(null);
  const [autoRules, setAutoRules] = React.useState<
    readonly { kind: string; enabled: boolean; reason?: string; created: number; skipped: number }[]
  >([]);
  const [now] = React.useState(() => systemClock.now());

  /*
   * Makléř nesmí z „Moje“ ven. Reset je v RENDERU, ne v `useEffect`: stav
   * plněný z props/session v efektu po připojení je závod, který spadne až
   * pod plnou bránou (nález UXR3b, `rh-a2-*` obrazovky).
   */
  if (brokerOnlyMine && !mineOnly) setMineOnly(true);

  // 8px aktivace: bez ní PointerSensor pohltí běžný klik na kartu a „klik ->
  // detail" by se nikdy nedosáhl (stejný důvod jako v `zakazky.tsx`).
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      /*
       * Sloupce a úkoly jdou dvěma requesty, ne jedním: sloupce jsou
       * konfigurace životního cyklu (malá, jinde v UI) a úkoly fronta.
       * Krok TAB1 bod 5: karta nese ŠTÍTEK ZAKÁZKY a AVATAR ŘEŠITELE, takže
       * k tomu přibyly zakázky a členové kanceláře — jedno dávkové volání
       * každé, ne dotaz na kartu (tentýž důvod jako O6a u kanbanu).
       */
      const [columnResult, taskResult, caseResult, memberResult, mailboxResult, mailResult] = await Promise.all([
        listTaskColumns(),
        listTasks({ limit: TASK_PAGE_LIMIT, ...(searchQuery !== '' ? { q: searchQuery } : {}) }),
        listCases({ limit: CASE_PAGE_LIMIT }).catch(() => ({ items: [] as KanbanCase[], nextCursor: null })),
        listMembers().catch(() => [] as TenantMember[]),
        // Krok AS1: schránka je doplněk tabule — její výpadek tabuli neshodí.
        getMailbox().catch(() => null),
        getMailboxCards().catch(() => [] as MailCardDto[]),
      ]);
      setMailbox(mailboxResult);
      setMailCards(mailboxResult?.connected === true ? mailResult : []);
      setColumns(columnResult);
      setTasks(taskResult.items);
      setCases(caseResult.items);
      setMembers(memberResult);
      setTruncated(taskResult.nextCursor !== null);
    } catch (e) {
      setLoadError(e instanceof DomainApiError ? e.message : t('webAdmin.tasks.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t, searchQuery]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    const trimmed = search.trim();
    if (trimmed === searchQuery) return;
    const timer = setTimeout(() => setSearchQuery(trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, searchQuery]);

  /* O8: zakázky se zástupným názvem dostanou adresu nemovitosti — štítek na
     kartě úkolu nesmí nést interní id (tentýž důvod jako v `/zakazky`). */
  React.useEffect(() => {
    const propertyIds = cases
      .filter((c) => isInternalIdTitle(c.title))
      .map((c) => c.propertyId)
      .filter((id): id is string => id !== null);
    if (propertyIds.length === 0) {
      setAddressByProperty(new Map());
      return;
    }
    let cancelled = false;
    void fetchPropertyAddresses(propertyIds)
      .then((found) => {
        if (!cancelled) setAddressByProperty(found);
      })
      .catch(() => {
        if (!cancelled) setAddressByProperty(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [cases]);

  const nameTexts = React.useMemo(
    () => ({
      untitled: t('webAdmin.kanban.untitled'),
      untitledHint: (id: string) => t('webAdmin.kanban.untitledHint', { id }),
    }),
    [t],
  );

  const caseNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const item of cases) map.set(item.id, caseDisplayName(item, addressByProperty, nameTexts).text);
    return map;
  }, [cases, addressByProperty, nameTexts]);

  const memberNameById = React.useMemo(
    () => new Map(members.map((m) => [m.userId, m.name])),
    [members],
  );

  /** Vše kromě filtru zdroje — z toho se počítají čísla na čipech. */
  const beforeSourceFilter = React.useMemo(() => {
    if (tasks === null) return [];
    return tasks.filter((task) => {
      if (mineOnly && task.ownerUserId !== currentUserId) return false;
      return matchesTaskQuery(task, search);
    });
  }, [tasks, mineOnly, search, currentUserId]);

  const filtered = React.useMemo(
    () => (source === 'email' ? [] : beforeSourceFilter.filter((task) => matchesTaskSource(task, source))),
    [beforeSourceFilter, source],
  );

  /*
   * Krok AS1 bod 5 — karty „Nové z e-mailu". Jsou to e-maily PŘIHLÁŠENÉHO
   * makléře (server jiné nevydá), takže přepínač Moje / Celá kancelář na ně
   * nemá vliv; hledání ano (předmět a odesílatel).
   */
  const mailboxConnected = mailbox?.connected === true;
  const emailBeforeSource = React.useMemo(() => {
    const query = search.trim().toLocaleLowerCase('cs');
    return mailColumnCards(mailCards).filter(
      (card) =>
        query === '' ||
        [card.subject, card.fromName, card.fromEmail].some((v) => (v ?? '').toLocaleLowerCase('cs').includes(query)),
    );
  }, [mailCards, search]);
  const emailOnBoard = source === 'all' || source === 'email' ? emailBeforeSource : [];
  const strip = mailboxStrip(mailbox, mailColumnCards(mailCards).length);

  /*
   * Krok TAB1 bod 4 — „HOTOVO DNES". Starší hotové se do sloupce nedostanou,
   * dokud si je makléř nevyžádá; jinak sloupec po měsíci provozu přeroste
   * obrazovku a přestane být zprávou o dnešku.
   */
  const olderDone = React.useMemo(
    () => filtered.filter((task) => task.status === 'done' && !isDoneToday(task, now)),
    [filtered, now],
  );
  const onBoard = React.useMemo(() => {
    if (showOlderDone) return filtered;
    const hidden = new Set(olderDone.map((task) => task.id));
    return filtered.filter((task) => !hidden.has(task.id));
  }, [filtered, olderDone, showOlderDone]);

  /**
   * Krok TOKYFIX3 (překážka P2/6, měření 16. 10. 2026) — TABULE NESMÍ ŘÍCT
   * „nic tu není", KDYŽ SE JEŠTĚ NEZEPTALA.
   *
   * Hledání jde od TOKYFIX2 na server, ale se zpožděním `SEARCH_DEBOUNCE_MS`.
   * Mezi napsáním dotazu a odpovědí serveru tabule filtrovala NAČTENOU
   * stránku (prvních 100 úkolů podle termínu) a vykreslila prázdný stav —
   * takže makléřce, která právě založila úkol s termínem za tři dny
   * (v kanceláři 115 úkolů, ten její 101. v pořadí), obrazovka tvrdila, že
   * žádný takový úkol není. Dokud dotaz běží, je na obrazovce „Hledám…",
   * ne odpověď, kterou server nedal.
   */
  const searchPending = search.trim() !== searchQuery || (loading && searchQuery !== '');

  const groups = React.useMemo(() => groupTasksByColumn(onBoard, columns), [onBoard, columns]);
  // `Set<string>` schválně: `groupTasksByColumn` zná i sloupce „navíc" (viz
  // jeho orphans), takže porovnáváme řetězce, ne výčet.
  const knownColumnKeys = React.useMemo(() => new Set<string>(columns.map((c) => c.key)), [columns]);

  /** Krok TAB1 bod 2: hlavička. Počty platí pro aktuální výběr (Moje / celá kancelář). */
  const openCount = openTaskCount(beforeSourceFilter);
  const overdueCount = overdueTaskCount(beforeSourceFilter, now);

  const sourceCounts = React.useMemo(
    () => ({
      all: beforeSourceFilter.length + emailBeforeSource.length,
      email: emailBeforeSource.length,
      auto: beforeSourceFilter.filter((task) => taskOriginKey(task.originKind) !== null).length,
      manual: beforeSourceFilter.filter((task) => taskOriginKey(task.originKind) === null).length,
    }),
    [beforeSourceFilter, emailBeforeSource],
  );

  function handleDragStart(event: DragStartEvent): void {
    setActionError(null);
    const data = event.active.data.current as { allowedTransitions?: readonly string[] } | undefined;
    setDraggingAllowed(data?.allowedTransitions ?? []);
  }

  /**
   * Krok TAB1 bod 4 — JEDNA FUNKCE PŘESUNU. Tažení na tabuli i kontextová
   * akce karty („Hotovo“) jdou tudy; rozhodnutí, jestli je cíl povolený,
   * dělá `decideTaskMove` (`lib/task-board.ts`), stejné jako na Dnešku.
   */
  const moveTask = React.useCallback(
    async (
      task: { readonly id: string; readonly status: string; readonly allowedTransitions: readonly string[] },
      to: string,
    ): Promise<void> => {
      const decision = decideTaskMove(task, to);
      if (!decision.ok) return;
      setBusyTaskId(task.id);
      try {
        const updated = await transitionTask(task.id, decision.to);
        setTasks((prev) => (prev === null ? prev : prev.map((row) => (row.id === updated.id ? updated : row))));
      } catch (e) {
        setActionError(e instanceof DomainApiError ? e.message : t('webAdmin.tasks.actionFailed'));
      } finally {
        setBusyTaskId(null);
      }
    },
    [t],
  );

  async function handleDragEnd(event: DragEndEvent): Promise<void> {
    setDraggingAllowed(null);
    const { active, over } = event;
    if (over === null) return;
    const data = active.data.current as
      | { status?: string; allowedTransitions?: readonly string[] }
      | undefined;
    if (data?.status === undefined) return;
    await moveTask(
      { id: String(active.id), status: data.status, allowedTransitions: data.allowedTransitions ?? [] },
      String(over.id),
    );
  }

  function onDrawerChanged(updated: Task): void {
    setTasks((prev) => (prev === null ? prev : prev.map((task) => (task.id === updated.id ? updated : task))));
  }

  function onDrawerDeleted(taskId: string): void {
    setOpenId(null);
    setTasks((prev) => (prev === null ? prev : prev.filter((task) => task.id !== taskId)));
  }

  async function runAuto(): Promise<void> {
    setAutoBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      const result = await runAutoTaskGeneration('cs');
      setAutoRules(result.rules);
      setNotice(
        t('webAdmin.tasks.autoRunNotice', {
          created: String(result.created),
          skipped: String(result.skipped),
        }),
      );
      await load();
    } catch (e) {
      setActionError(e instanceof DomainApiError ? e.message : t('webAdmin.tasks.actionFailed'));
    } finally {
      setAutoBusy(false);
    }
  }

  const openCase = React.useCallback(
    (task: Task): (() => void) | null => {
      const caseId = task.caseId;
      if (caseId === null || !caseNameById.has(caseId)) return null;
      return () => void navigate({ to: '/zakazky/$id', params: { id: caseId } });
    },
    [caseNameById, navigate],
  );

  /**
   * Krok TAB1 bod 5 — JEDNA KONTEXTOVÁ AKCE KARTY.
   *
   *  - „K udělání“ / „Dělám“ → **Hotovo** (tentýž přesun jako tažení i Dnešek),
   *  - „Čeká na druhé“ → **Připomenout**,
   *  - „Hotovo dnes“ → žádná (odškrtnuté se vrací tažením nebo z drawru).
   *
   * ROZHODNUTÍ TAB1-PRIPOMENOUT: „Připomenout“ NEODESÍLÁ nic. REOS dnes
   * žádné e-maily ani SMS klientům neposílá (poznámka 4 u předlohy to říká
   * i o konceptech odpovědí) a vymyslet odeslání by znamenalo slíbit funkci,
   * která neexistuje. Akce proto otevře DETAIL ZAKÁZKY, kde jsou kontakty,
   * dokumenty a historie — tedy místo, kde makléř klienta skutečně urguje.
   * Úkol bez zakázky otevře svůj detail (drawer).
   */
  const taskAction = React.useCallback(
    (task: Task): { readonly label: string; readonly onAction: () => void } | null => {
      if (busyTaskId === task.id) return null;
      if (task.status === 'todo' || task.status === 'doing') {
        if (!task.allowedTransitions.includes('done')) return null;
        return { label: t('webAdmin.tasks.actionDone'), onAction: () => void moveTask(task, 'done') };
      }
      if (task.status === 'waiting') {
        const toCase = openCase(task);
        return {
          label: t('webAdmin.tasks.actionRemind'),
          onAction: toCase ?? (() => setOpenId(task.id)),
        };
      }
      return null;
    },
    [busyTaskId, moveTask, openCase, t],
  );

  /** Krok AS1: karta zmizí ze sloupce hned; stav karty (jen ten) mění server. */
  async function resolveMail(card: MailCardDto, action: 'done' | 'dismissed'): Promise<void> {
    setBusyMailId(card.id);
    setActionError(null);
    try {
      const updated = await resolveMailCard(card.id, action);
      setMailCards((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
    } catch (e) {
      setActionError(e instanceof DomainApiError ? e.message : t('webAdmin.tasks.actionFailed'));
    } finally {
      setBusyMailId(null);
    }
  }

  /**
   * Krok MAIL1 bod 2 — „Odpovědět" na kartě e-mailu.
   *
   * Odpověď jde ze schránky makléře. Když je odesílání vypnuté nebo narazí na
   * limit, karta to ŘEKNE (`blockReason`) — nikdy se netváří, že odpověď
   * odešla. Odkaz tady kopírovat není co (odpověď není odkaz), takže se
   * ukazuje jen samotný důvod.
   */
  async function replyMail(card: MailCardDto, text: string): Promise<void> {
    setBusyMailId(card.id);
    setActionError(null);
    try {
      const result = await replyMailCard(card.id, text);
      setReplyNotes((prev) => ({
        ...prev,
        [card.id]: result.sent
          ? t('webAdmin.mailbox.replySent')
          : t(clientMailReasonKey(result.blockReason)),
      }));
    } catch (e) {
      setActionError(e instanceof DomainApiError ? e.message : t('webAdmin.tasks.actionFailed'));
    } finally {
      setBusyMailId(null);
    }
  }

  /**
   * Krok NAP4 bod A9 — z NÁVRHU termínu v e-mailu událost v kalendáři.
   *
   * Okamžik (`startsAt`) počítá server v pásmu kanceláře, UI ho jen předá —
   * dvě přepočty pásma by se rozešly (nález „kalendář ignoruje Z"). Vazba na
   * zakázku jde dál, když ji karta nese: událost pak visí u té zakázky.
   */
  async function createEventFromProposal(card: MailCardDto, proposal: ProposedEventDto): Promise<void> {
    const name = card.parsedInquiry?.name ?? card.fromName;
    await createCalendarEvent({
      title:
        name !== null && name !== ''
          ? t('webAdmin.mailbox.proposedEvents.eventTitle', { name })
          : t('webAdmin.mailbox.proposedEvents.eventTitleNoName'),
      eventType: 'meeting',
      status: 'confirmed',
      startsAt: proposal.startsAt,
      reminderMinutes: 0,
      ...(card.caseId !== null ? { caseId: card.caseId } : {}),
    });
  }

  /**
   * Krok PROP5 bod 5 — „Uložit přílohu k zakázce".
   *
   * Server si přílohu dotáhne ze schránky (jen ke čtení), prožene ji
   * antivirem a uloží jako dokument zakázky s odkazem na původní e-mail.
   * Když to nejde (antivirus, typ, e-mail už ve schránce není), karta to
   * ŘEKNE — nikdy se netváří, že dokument u zakázky je.
   */
  async function saveAttachment(card: MailCardDto, index: number): Promise<void> {
    setBusyMailId(card.id);
    setActionError(null);
    try {
      const result = await saveMailAttachment(card.id, index);
      setAttachmentNotes((prev) => ({
        ...prev,
        [card.id]: result.duplicate
          ? t('webAdmin.mailbox.attachmentDuplicate', { file: result.filename })
          : t('webAdmin.mailbox.attachmentSaved', { file: result.filename }),
      }));
    } catch (e) {
      setAttachmentNotes((prev) => ({
        ...prev,
        [card.id]: t(mailAttachmentReasonKey(e instanceof DomainApiError ? e.type : null)),
      }));
    } finally {
      setBusyMailId(null);
    }
  }

  /**
   * Hlavní akce karty (viz `mailPrimaryAction`). Nic z toho NEODESÍLÁ e-mail
   * a nic nemění fakturu: „Ověřit platbu" otevře Fakturaci, „Založit
   * poptávku" založí poptávku v CRM a kartu odškrtne.
   */
  async function mailPrimary(card: MailCardDto, action: MailPrimaryAction, chosenCaseId?: string): Promise<void> {
    switch (action.kind) {
      case 'create_demand': {
        setBusyMailId(card.id);
        setActionError(null);
        try {
          // Notifikace portálu chodí z adresy portálu, ne od zájemce — kontakt
          // z OBÁLKY do poptávky nepatří. Krok SR2: tělo notifikace ale kontakt
          // nese v doloženém tvaru (`parsePortalInquiryText`, @reos/broker-inbox)
          // - pokud ho server rozpoznal, doplní se odtud; jinak (přímý dotaz,
          // nebo neznámý formát) zůstává dřívější chování.
          const direct = card.portal === null;
          const parsed = card.parsedInquiry;
          await createDemand({
            title: card.subject ?? t('webAdmin.mailbox.card.noSubject'),
            source: direct ? 'web' : 'portal',
            ...(direct && card.fromName !== null ? { contactName: card.fromName } : {}),
            ...(direct && card.fromEmail !== null ? { contactEmail: card.fromEmail } : {}),
            ...(!direct && parsed?.name != null ? { contactName: parsed.name } : {}),
            ...(!direct && parsed?.email != null ? { contactEmail: parsed.email } : {}),
            ...(!direct && parsed?.phone != null ? { contactPhone: parsed.phone } : {}),
            ...(!direct && parsed?.message != null ? { wantedNotes: parsed.message } : {}),
            ...(card.caseId !== null ? { caseId: card.caseId } : {}),
          });
          const updated = await resolveMailCard(card.id, 'done');
          setMailCards((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
          setNotice(t('webAdmin.mailbox.demandCreated'));
        } catch (e) {
          setActionError(e instanceof DomainApiError ? e.message : t('webAdmin.tasks.actionFailed'));
        } finally {
          setBusyMailId(null);
        }
        return;
      }
      case 'open_case':
        void navigate({ to: '/zakazky/$id', params: { id: action.caseId } });
        return;
      case 'verify_payment':
        void navigate({ to: '/fakturace' });
        return;
      case 'choose_case':
        if (chosenCaseId !== undefined) void navigate({ to: '/zakazky/$id', params: { id: chosenCaseId } });
        return;
      case 'done':
        await resolveMail(card, 'done');
        return;
    }
  }

  const columnHint = React.useCallback(
    (key: string): string | undefined => (key === 'waiting' ? t('webAdmin.tasks.waitingHint') : undefined),
    [t],
  );

  /**
   * Krok TAB1 bod 4 — NADPIS SLOUPCE „Hotovo dnes".
   *
   * Popisky sloupců jdou z API (`/v1/crm-task-columns`, katalog
   * `crm.task.status.*`) a tady se nepřepisují — JEDINÁ výjimka je `done`:
   * stav se jmenuje „Hotovo", ale TENHLE SLOUPEC ukazuje jen dnešek (starší
   * jsou za odkazem pod ním). Nadpis „Hotovo" by u tří karet tvrdil, že
   * kancelář za celou dobu zavřela tři úkoly.
   */
  const columnLabel = React.useCallback(
    (column: { readonly key: string; readonly label: string }): string =>
      column.key === 'done' && !showOlderDone ? t('webAdmin.tasks.columnDoneToday') : column.label,
    [showOlderDone, t],
  );

  /** Odkaz „Starší hotové (N)“ pod sloupcem „Hotovo dnes“ (předloha, bod 4). */
  const columnFooter = React.useCallback(
    (key: string): React.ReactNode => {
      if (key !== 'done' || olderDone.length === 0) return null;
      return (
        <button
          type="button"
          className="rh-a2-kcol__more"
          data-testid="task-older-done"
          onClick={() => setShowOlderDone((v) => !v)}
        >
          {showOlderDone
            ? t('webAdmin.tasks.olderDoneHide')
            : t('webAdmin.tasks.olderDone', { count: String(olderDone.length) })}
        </button>
      );
    },
    [olderDone.length, showOlderDone, t],
  );

  function changeView(next: TaskView): void {
    setView(next);
    writeTaskViewPreference(next);
  }

  /**
   * Popisek sloupce, ve kterem ukol stoji — pro seznam (bod 2). Retezce jdou
   * z API (`/v1/crm/task-columns`), tedy z TEHOZ zdroje jako nadpisy tabule;
   * neznamy stav se vypise, jak prisel (nova verze API nesmi shodit seznam).
   */
  const statusLabelByKey = React.useMemo(() => new Map(columns.map((c) => [c.key, c.label])), [columns]);

  /**
   * Sloupec „Nové z e-mailu" — JEDEN zapis pro oba pohledy (bod 2). Dve kopie
   * by znamenaly dve stejna `data-testid` v DOM a druhe misto, kde se meni
   * chovani karty.
   */
  const emailColumn =
    mailboxConnected && (source === 'all' || source === 'email') ? (
      <div className="rh-a2-kcol rh-a2-kcol--email" data-testid="task-column-email">
        <div className="rh-a2-kcol__head">
          <span className="rh-a2-dot rh-a2-dot--email" aria-hidden="true" />
          <span>{t('webAdmin.tasks.columnEmail')}</span>
          <span className="rh-a2-count" data-testid="task-column-count-email">
            {emailOnBoard.length}
          </span>
        </div>
        <div className="rh-a2-kcol__body">
          {emailOnBoard.map((card) => (
            <MailCard
              key={card.id}
              card={card}
              now={now}
              caseName={(id) => caseNameById.get(id) ?? null}
              busy={busyMailId === card.id}
              onPrimary={(c, a, chosen) => void mailPrimary(c, a, chosen)}
              onResolve={(c, a) => void resolveMail(c, a)}
              canReply={mailbox?.canSend === true}
              onReply={(c, text) => void replyMail(c, text)}
              replyNote={replyNotes[card.id] ?? null}
              onSaveAttachment={(c, index) => void saveAttachment(c, index)}
              attachmentNote={attachmentNotes[card.id] ?? null}
              onSuggestReply={(c) => suggestReplyDraft(c.id)}
              onProposedEvents={(c) => listProposedEvents(c.id)}
              onCreateEvent={createEventFromProposal}
            />
          ))}
        </div>
        {emailOnBoard.length === 0 ? (
          <p className="rh-a2-kcol__hint" data-testid="task-column-email-empty">
            {t('webAdmin.tasks.emailEmpty')}
          </p>
        ) : null}
      </div>
    ) : null;

  return (
    <>
      <PageHead
        title={t('webAdmin.tasks.pageTitle')}
        titleTestId="ukoly-title"
        breadcrumb={formatFullDate(now)}
        subtitle={
          /*
            Krok TAB1 bod 2: „N otevřených · N po termínu", počet po termínu
            ČERVENĚ (předloha). Jsou to DVĚ celé věty z katalogu spojené
            oddělovačem, ne jedna rozsekaná na kusy — jazyk, který by potřeboval
            jiný tvar čísla nebo jiné pořadí, by na slepování doplatil.
          */
          <span data-testid="task-header-counts">
            {t('webAdmin.tasks.headerOpen', { open: openCount })}
            {' · '}
            <span
              className={overdueCount > 0 ? 'rh-a2-bad' : undefined}
              data-testid="task-header-overdue"
              data-count={String(overdueCount)}
            >
              {t('webAdmin.tasks.headerOverdue', { overdue: overdueCount })}
            </span>
            {mailboxConnected ? (
              <>
                {' · '}
                <span className="rh-a2-mnew" data-testid="task-header-email">
                  {t('webAdmin.tasks.headerEmail', { count: emailBeforeSource.length })}
                </span>
              </>
            ) : null}
          </span>
        }
        action={
          <>
            <button
              type="button"
              className="rh-a2-btn"
              disabled={autoBusy}
              data-testid="task-auto-run"
              title={t('webAdmin.tasks.autoRunHint')}
              onClick={() => void runAuto()}
            >
              <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
                <Bolt size={15} strokeWidth={1.9} />
              </span>
              {t('webAdmin.tasks.autoRun')}
            </button>
            <button
              type="button"
              className="rh-a2-btn rh-a2-btn--primary"
              data-testid="task-new"
              onClick={() => setShowForm((v) => !v)}
            >
              <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
                <Plus size={15} strokeWidth={2.2} />
              </span>
              {t('webAdmin.tasks.newTask')}
            </button>
          </>
        }
      />

      <div className="rh-a2-page rh-a2-page--wide" data-testid="ukoly-page">
        {strip !== null ? (
          <MailboxStrip model={strip} onSettings={() => void navigate({ to: '/nastaveni', hash: 'schranka' })} />
        ) : null}
        <div className="rh-a2-filters">
          {/* Krok TAB1 bod 3: makléř přepínač nedostane vůbec — nabízet ho a
              pak nic nezměnit by bylo horší než ho nemít (viz
              `BROKER_ONLY_MINE_ROLES`). */}
          {brokerOnlyMine ? (
            <span className="rh-a2-meta" data-testid="task-filter-mine-only">
              {t('webAdmin.tasks.onlyMineRole')}
            </span>
          ) : (
            <div className="rh-a2-seg" role="group" aria-label={t('webAdmin.kanban.brokerFilterAriaLabel')}>
              <button
                type="button"
                className={mineOnly ? 'is-on' : ''}
                onClick={() => setMineOnly(true)}
                aria-pressed={mineOnly}
                data-testid="task-filter-mine"
              >
                {t('webAdmin.kanban.mine')}
              </button>
              <button
                type="button"
                className={!mineOnly ? 'is-on' : ''}
                onClick={() => setMineOnly(false)}
                aria-pressed={!mineOnly}
                data-testid="task-filter-office"
              >
                {t('webAdmin.kanban.wholeOffice')}
              </button>
            </div>
          )}

          <span className="rh-a2-filters__sep" />

          {TASK_SOURCE_FILTERS.map((key) => (
            <button
              key={key}
              type="button"
              className={`rh-a2-chip${source === key ? ' is-on' : ''}`}
              onClick={() => setSource(key)}
              aria-pressed={source === key}
              data-testid={`task-chip-${key}`}
            >
              {key === 'auto' ? (
                <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
                  <Bolt size={13} strokeWidth={1.9} />
                </span>
              ) : null}
              {key === 'manual' ? (
                <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
                  <User size={13} strokeWidth={1.9} />
                </span>
              ) : null}
              {key === 'all'
                ? t('webAdmin.tasks.filterAll')
                : key === 'auto'
                  ? t('webAdmin.tasks.filterAuto')
                  : t('webAdmin.tasks.filterManual')}{' '}
              <span className="rh-a2-chip__n">{sourceCounts[key]}</span>
            </button>
          ))}
          {/* Krok AS1: čip „E-mail" jen s připojenou schránkou (TAB1: žádný falešný čip). */}
          {mailboxConnected ? (
            <button
              type="button"
              className={`rh-a2-chip${source === 'email' ? ' is-on' : ''}`}
              onClick={() => setSource('email')}
              aria-pressed={source === 'email'}
              data-testid="task-chip-email"
            >
              <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
                <Mail size={13} strokeWidth={1.9} />
              </span>
              {t('webAdmin.tasks.filterEmail')} <span className="rh-a2-chip__n">{sourceCounts.email}</span>
            </button>
          ) : null}

          <label className="rh-a2-field">
            <span className="sr-only">{t('webAdmin.tasks.searchLabel')}</span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('webAdmin.tasks.searchLabel')}
              data-testid="task-search"
              aria-label={t('webAdmin.tasks.searchLabel')}
            />
          </label>

          {/* Krok UXF5 bod 2: Seznam / Kanban — tentyz prepinac jako `/zakazky`
              a `/poptavky` (tytez retezce `webAdmin.kanban.view*`). */}
          <div className="rh-a2-seg rh-a2-filters__right" role="group" aria-label={t('webAdmin.kanban.viewSwitchLabel')}>
            <button
              type="button"
              className={view === 'list' ? 'is-on' : ''}
              onClick={() => changeView('list')}
              aria-pressed={view === 'list'}
              data-testid="task-view-list"
            >
              {t('webAdmin.kanban.viewList')}
            </button>
            <button
              type="button"
              className={view === 'kanban' ? 'is-on' : ''}
              onClick={() => changeView('kanban')}
              aria-pressed={view === 'kanban'}
              data-testid="task-view-kanban"
            >
              {t('webAdmin.kanban.viewKanban')}
            </button>
          </div>
        </div>

        {showForm && (
          <TaskForm
            members={members}
            currentUserId={currentUserId}
            onCreated={() => {
              setNotice(t('webAdmin.tasks.form.created'));
              setShowForm(false);
              void load();
            }}
            onError={(message) => setActionError(message)}
          />
        )}

        {notice !== null && (
          <p role="status" className="rh-a2-status" data-testid="task-notice">
            {notice}
          </p>
        )}
        {loading && (
          <p role="status" className="rh-a2-status">
            {t('webAdmin.tasks.loading')}
          </p>
        )}
        {loadError !== null && (
          <p role="alert" className="rh-a2-error" data-testid="task-load-error">
            {loadError}
          </p>
        )}
        {actionError !== null && (
          <p role="alert" className="rh-a2-error" data-testid="task-action-error">
            {actionError}
          </p>
        )}
        {truncated && (
          <p className="rh-a2-status" data-testid="task-truncated">
            {t('webAdmin.tasks.truncated', { limit: String(TASK_PAGE_LIMIT) })}
          </p>
        )}

        {/*
          Výsledek posledního běhu automatiky. Vypnuté zdroje se nesmějí
          schovat: „tabule sama nedoplnila termíny" musí být na obrazovce
          vidět, jinak si kancelář myslí, že automatika běží (viz
          ROZHODNUTI CRM2-3 v `auto-task-rules.ts`).
        */}
        {autoRules.length > 0 && (
          <section className="rh-task-auto" data-testid="task-auto-rules">
            <h2 className="rh-a2-meta">{t('webAdmin.tasks.autoRulesHeading')}</h2>
            <ul className="rh-a2-meta" data-testid="task-auto-rules-list">
              {autoRules.map((rule) => {
                const key = taskOriginKey(rule.kind);
                return (
                  <li key={rule.kind} data-testid={`task-auto-rule-${rule.kind}`}>
                    {key === null
                      ? rule.kind
                      : t(`webAdmin.tasks.origin.${key}` as 'webAdmin.tasks.origin.case_control')}
                    {' — '}
                    {rule.enabled
                      ? `${t('webAdmin.tasks.autoRuleOn')} (+${String(rule.created)})`
                      : t('webAdmin.tasks.autoRuleOff', {
                          reason:
                            rule.reason === 'stage_missing'
                              ? t('webAdmin.tasks.autoReasonStageMissing')
                              : t('webAdmin.tasks.autoReasonSourceTableMissing'),
                        })}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {searchPending && (
          <p role="status" className="rh-a2-status" data-testid="task-search-pending">
            {t('webAdmin.tasks.searching')}
          </p>
        )}

        {!loading && !searchPending && loadError === null && filtered.length === 0 && emailOnBoard.length === 0 ? (
          <div className="rh-a2-empty" data-testid="ukoly-empty">
            <p className="rh-a2-empty__title">{t('webAdmin.tasks.emptyTitle')}</p>
            <p>{t('webAdmin.tasks.emptyDescription')}</p>
          </div>
        ) : view === 'list' ? (
          /*
            Krok UXF5 bod 2: v seznamu stoji karty e-mailu NAD frontou ukolu
            (tytez `MailCard`, jeden DOM — viz `emailColumn`), protoze e-mail
            jeste ukol neni a do sloupcu „Úkol / zakázka / termín" nepatri.
          */
          <>
            {emailColumn}
            {source === 'email' ? null : (
              <TaskList
                items={onBoard}
                now={now}
                onOpen={setOpenId}
                caseName={(task) => (task.caseId === null ? null : (caseNameById.get(task.caseId) ?? null))}
                onOpenCase={openCase}
                ownerName={(task) =>
                  task.ownerUserId === null ? null : (memberNameById.get(task.ownerUserId) ?? null)
                }
                action={taskAction}
                statusLabel={(task) => statusLabelByKey.get(task.status) ?? task.status}
              />
            )}
            {source === 'email' || olderDone.length === 0 ? null : columnFooter('done')}
          </>
        ) : (
          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={(e) => void handleDragEnd(e)}>
            <div
              className={`rh-a2-kanban rh-a2-kanban--tasks${mailboxConnected && source !== 'email' && source !== 'auto' && source !== 'manual' ? ' rh-a2-kanban--tasks5' : ''}`}
              aria-label={t('webAdmin.tasks.boardLabel')}
            >
              {emailColumn}
              {source === 'email' ? null : groups.map(({ column, items }) => (
                <TaskColumn
                  key={column.key}
                  column={{ ...column, label: columnLabel(column) }}
                  items={items}
                  now={now}
                  onOpen={setOpenId}
                  dropAllowed={draggingAllowed === null ? undefined : draggingAllowed.includes(column.key)}
                  unknown={!knownColumnKeys.has(column.key)}
                  hint={columnHint(column.key)}
                  footer={columnFooter(column.key)}
                  caseName={(task) => (task.caseId === null ? null : (caseNameById.get(task.caseId) ?? null))}
                  onOpenCase={openCase}
                  ownerName={(task) =>
                    task.ownerUserId === null ? null : (memberNameById.get(task.ownerUserId) ?? null)
                  }
                  action={taskAction}
                />
              ))}
            </div>
          </DndContext>
        )}

        {openId !== null && (
          <TaskDrawer
            taskId={openId}
            columns={columns}
            onClose={() => setOpenId(null)}
            onChanged={onDrawerChanged}
            onDeleted={onDrawerDeleted}
          />
        )}
      </div>
    </>
  );
}

/**
 * Ruční založení úkolu (telefon, schůzka, dohoda s vedoucím).
 *
 * Krok UXP2 (P2-13): „Makléř" byl volný text s placeholderem „Bez makléře" —
 * úkol se tak zakládal bez majitele a v pohledu „Moje" (filtr na
 * `ownerUserId === currentUserId`) zmizel. Výchozí hodnota je PŘIHLÁŠENÝ
 * uživatel, pole je výběr ze stejného seznamu členů jako zbytek obrazovky.
 */
function TaskForm(props: {
  readonly members: readonly TenantMember[];
  readonly currentUserId: string | null;
  readonly onCreated: () => void;
  readonly onError: (message: string) => void;
}): React.JSX.Element {
  const t = useT();
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [dueAt, setDueAt] = React.useState('');
  const [ownerUserId, setOwnerUserId] = React.useState(() => props.currentUserId ?? '');
  const [busy, setBusy] = React.useState(false);

  async function submit(): Promise<void> {
    setBusy(true);
    try {
      await createTask({
        title: title.trim(),
        ...(description.trim() === '' ? {} : { description: description.trim() }),
        ...(ownerUserId.trim() === '' ? {} : { ownerUserId: ownerUserId.trim() }),
        dueAt: fromDateTimeLocalValue(dueAt),
      });
      props.onCreated();
    } catch (e) {
      props.onError(e instanceof DomainApiError ? e.message : t('webAdmin.tasks.drawer.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rh-demand-form" data-testid="task-form" aria-label={t('webAdmin.tasks.newTask')}>
      <div className="rh-demand-form__row">
        <label>
          {t('webAdmin.tasks.form.titleLabel')}
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('webAdmin.tasks.form.titlePlaceholder')}
            data-testid="task-form-title"
          />
        </label>
        <label>
          {t('webAdmin.tasks.form.dueLabel')}
          <DateTimeField value={dueAt} onValueChange={setDueAt} testId="task-form-due" />
        </label>
        <label>
          {t('webAdmin.tasks.form.ownerLabel')}
          <select
            value={ownerUserId}
            onChange={(e) => setOwnerUserId(e.target.value)}
            data-testid="task-form-owner"
          >
            <option value="">{t('webAdmin.tasks.noOwner')}</option>
            {props.members.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label>
        {t('webAdmin.tasks.form.descriptionLabel')}
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="task-form-description" />
      </label>
      <button
        type="button"
        className="rh-a2-btn rh-a2-btn--primary"
        disabled={busy || title.trim() === ''}
        data-testid="task-form-submit"
        onClick={() => void submit()}
      >
        {t('webAdmin.tasks.form.submit')}
      </button>
    </section>
  );
}
